import React from "react";
import { Link } from "react-router-dom";
import logoSrc from "@/assets/forgedops-logo.png";
import { useTenant } from "@/lib/useTenant";

/**
 * ForgedOpsAttribution — platform ownership + legal footer line.
 *
 * Tenant-aware: brand_name + owner_name + owner_website + logo_emblem
 * are all read from `useTenant()` so a white-label deployment swaps
 * branding everywhere this component appears, without rebuild.
 *
 * Single-entity model: ForgedOps LLC owns and operates the platform
 * by default. Three render variants:
 *   • global — every-page footer with legal links
 *   • login  — subtle line beneath login forms
 *   • admin  — slightly stronger line for admin/PM hubs
 */

export function ForgedOpsAttribution({ variant = "global", className = "" }) {
  const tenant = useTenant();
  const brandShort = tenant.brand_name_short || "ForgedOps";
  const ownerName  = tenant.owner_name       || "ForgedOps LLC";
  const ownerSite  = tenant.owner_website    || "https://forgedopshq.com";
  const emblem     = tenant.logo_emblem      || logoSrc;
  const siteLabel  = ownerSite.replace(/^https?:\/\//, "");

  if (variant === "login") {
    return (
      <div
        className={`flex items-center justify-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 ${className}`}
        data-testid="forgedops-attr-login"
      >
        <img
          src={emblem}
          alt={brandShort}
          className="h-5 w-auto opacity-80"
        />
        <span>Built by {ownerName} · {siteLabel}</span>
      </div>
    );
  }

  if (variant === "admin") {
    return (
      <div
        className={`flex flex-col sm:flex-row items-center justify-center gap-3 ${className}`}
        data-testid="forgedops-attr-admin"
      >
        <img
          src={emblem}
          alt={brandShort}
          className="h-7 w-auto opacity-90"
        />
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600 font-bold">
          Operational infrastructure by {ownerName}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`text-center font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400 ${className}`}
      data-testid="forgedops-attr-global"
    >
      © {new Date().getFullYear()} {ownerName} · All rights reserved · Operational infrastructure for heavy-civil contractors ·{" "}
      <a
        href={ownerSite}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:text-slate-700 underline-offset-2 hover:underline"
      >
        {siteLabel}
      </a>{" "}
      ·{" "}
      <Link
        to="/legal/terms"
        className="hover:text-slate-700 underline-offset-2 hover:underline"
      >
        Terms
      </Link>{" "}
      ·{" "}
      <Link
        to="/legal/privacy"
        className="hover:text-slate-700 underline-offset-2 hover:underline"
      >
        Privacy
      </Link>
    </div>
  );
}

export default ForgedOpsAttribution;
